import React, { useEffect, useState } from 'react';
import { DatePicker, Form, FormInstance } from "antd";
import { ExternalStateUpdateCallback, Initialization, LabelLocation, OnChangeListener, Validator } from "../../../pages/info-portal/InfoPortalInterfaces";
import dayjs, {Dayjs} from "dayjs";
import { IAppComponentProps } from "../../../components";
import { getRangePickerLocalization } from "../../../pages/info-portal/components/grid/CalendarUtils";

type FromToDateSelectionProperties = {
    initialization: Initialization,
    form: FormInstance<any>,
    externalStateUpdater?: ExternalStateUpdateCallback<any>,
    onFromDateChange?: OnChangeListener<Dayjs>,
    onToDateChange?: OnChangeListener<Dayjs>,
    validator?: Validator,
    initialValue?: Dayjs[],
    dateFormatter?: string,
    labelLocation?: LabelLocation,
    displayColon?: boolean,
    customHeading?: string

} & IAppComponentProps;

type BaseSelectionProperties = {
    fieldName: string,
    singleHeadingName: string,
    fromHeading: string,
    toHeading: string,
    properties: FromToDateSelectionProperties
};

function FromToDateSelection(
    {
        fromHeading,
        fieldName,
        singleHeadingName,
        toHeading,
        properties: {
            initialization,
            form,
            externalStateUpdater,
            onToDateChange,
            onFromDateChange,
            intl,
            validator,
            initialValue,
            dateFormatter,
            labelLocation,
            displayColon,
            customHeading
        }
    }: BaseSelectionProperties) {
    const [_, rerender] = useState<boolean>(false);
    const [fromDate, setFrom] = useState<Dayjs>(null);
    const [toDate, setTo] = useState<Dayjs>(null);
    const {RangePicker} = DatePicker;
    const forceRerender = () => rerender(current => !current);


    const selectedFromDate = (dayjs: Dayjs) => {
        setFrom(dayjs);
        onFromDateChange?.performAction(dayjs);
    };

    const selectedToDate = (dayjs: Dayjs) => {
        setTo(dayjs);
        onToDateChange?.performAction(dayjs);
    };

    const setInitialValues = () => {
        if (initialValue) {
            if (!form.getFieldValue(fieldName)) {
                const initialValues = {};
                initialValues[fieldName] = initialValue;
                form.setFieldsValue(initialValues);
            }
        }
    };
    const dateRangeChanged = (dateRange: [Dayjs, Dayjs]) => {
        selectedFromDate(dateRange && dateRange.length > 0 ? dateRange[0] : undefined);
        selectedToDate(dateRange && dateRange.length > 0 ? dateRange[1] : undefined);
    };

    useEffect(() => {
        externalStateUpdater?.register({
            performAction() {
                forceRerender();
            }
        });
        setInitialValues();
    }, []);

    function isToBeforeFrom(fromDate: Dayjs, toDate: Dayjs) {
        if (fromDate && toDate) {
            if (toDate.startOf("d").isBefore(fromDate.startOf("d"))) {
                return true;
            }
        }
        return false;
    }

    const getDateFormat = () => dateFormatter || "YYYY-MM-DD";

    const getLabel = () => {
        return customHeading || fromHeading;
    }

    const displayCorrectFormatOnHint = (heading: string) => {
        const language = localStorage.getItem("locale");

        if (language === "en") {
            return heading.replace("YYYY-MM-DD", getDateFormat());
        } else if (language === "de") {
            const germanFormat = getDateFormat().replaceAll("Y", "J").replaceAll("D", "T")
            return heading.replace("JJJJ-MM-TT", germanFormat);
        }

        return heading;
    }

    return <div>

        <Form.Item label={getLabel()}
                   className={"from-input"}
                   name={fieldName}
                   rules={[
                       {
                           required: validator?.isMandatory(fieldName),
                           message: intl.formatMessage({
                               id: "generic-requiredField-message",
                               defaultMessage: `Please Provide Required Field`
                           })
                       },
                       ({getFieldValue}) => ({
                           validator(_, value) {
                               if (value?.length > 1 && isToBeforeFrom(value[0], value[1])) {
                                   return Promise.reject(new Error("From date cannot be after to date"));
                               }
                               return Promise.resolve();
                           }
                       })
                   ]}>
            <RangePicker data-testid={`from-selection-${fieldName}`} allowClear={true} format={getDateFormat()}
                        showTime={true} showHour={true} showMinute={true}
                         placeholder={[displayCorrectFormatOnHint(intl.formatMessage({
                             id: "info-portal-range-picker-start-date-hint",
                             defaultMessage: `Start Date YYYY-MM-DD`
                         })),
                             displayCorrectFormatOnHint(intl.formatMessage({
                                 id: "info-portal-range-picker-end-date-hint",
                                 defaultMessage: `End Date YYYY-MM-DD`
                             }))]}
                         disabledDate={(current) => current.isAfter(dayjs())}
                         style={{width: "100%"}} onChange={dateRangeChanged}
                         locale={getRangePickerLocalization(intl, localStorage.getItem("locale") || "en")}

            />
        </Form.Item>


    </div>;
}

export function AuditDateFromToSelection(props: FromToDateSelectionProperties) {
    return <FromToDateSelection
        properties={props}
        fieldName={"auditDate"}
        singleHeadingName={props.intl.formatMessage({id: "grid-heading-auditDate"})}
        fromHeading={props.intl.formatMessage({
            id: "grid-heading-auditDate",
            defaultMessage: "Audit Date"
        })}
        toHeading={props.intl.formatMessage({id: "entitlement-audit-to-date", defaultMessage: "Audit Date"})}/>;
}